import { useState, useMemo, useCallback, useEffect } from 'react';
import { CheckCircle, Ban } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  getEffectiveAmountPaid,
  getAmountReservedOnInvoice,
  getPendingVerificationAmount,
  INVOICE_AMOUNT_EPS,
} from '@/lib/invoice-utils';
import type { Event, Invoice, Payment, RSVP } from '@/types';
import {
  BOOKING_FEE_LABEL,
  computeCreditRecoveryTotalPounds,
} from '@/lib/credit-booking-fee';
import { getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import { getInvoicePayerRsvp } from '@/lib/invoice-payer-rsvp';
import {
  computeInvoicePolicyDisplayTotal,
  countCanonicalInvoiceGuests,
  orderRsvpsForInvoice,
} from '@/lib/invoice-payment-breakdown';
import {
  computeInvoiceBaseAmountForPayer,
  countHostInvoiceCreditRecoverySlots,
} from '@/lib/ensure-invoice-for-rsvp';
import { showError, showSuccess, confirmAction } from '@/lib/swal';

export type CallableInvoicePayload = {
  id: string;
  eventId?: string;
  unitId?: string;
  ownerUid?: string;
  totalAmount?: number;
  amountPaid?: number;
  outstandingBalance?: number;
  status?: Invoice['status'];
  rsvps?: string[];
  paymentHistory?: Array<Record<string, unknown>>;
  paymentLink?: string;
  invoiceNumber?: string;
  accountingTotals?: Invoice['accountingTotals'];
  subtotal?: number;
  vatAmount?: number;
  createdAt?: string;
  updatedAt?: string;
  allInPriceBreakdown?: Invoice['allInPriceBreakdown'];
  pendingVerificationAmount?: number;
};

function paymentMethodLabel(method: Payment['method']): string {
  switch (method) {
    case 'bank_transfer':
      return 'Bank transfer';
    case 'stripe':
      return 'Stripe';
    case 'sumup':
      return 'SumUp';
    case 'slickpay':
      return 'SlickPay';
    case 'card':
    default:
      return 'Card';
  }
}

function paymentReference(payment: Payment): string {
  return (
    payment.transactionId ||
    payment.stripePaymentIntentId ||
    payment.sumupCheckoutId ||
    payment.slickpayInvoiceId ||
    '—'
  );
}

function parsePaymentRow(raw: Record<string, unknown>): Payment {
  const amount = Number(raw.amount) || 0;
  const processingFee = Number(raw.processingFee) || 0;
  let date = new Date();
  const d = raw.date;
  if (typeof d === 'string') {
    date = new Date(d);
  } else if (d && typeof d === 'object' && d !== null && 'toDate' in d && typeof (d as { toDate: () => Date }).toDate === 'function') {
    date = (d as { toDate: () => Date }).toDate();
  } else if (d instanceof Date) {
    date = d;
  }

  const method = (raw.method as Payment['method']) || 'bank_transfer';

  return {
    amount,
    date,
    method,
    transactionId: typeof raw.transactionId === 'string' ? raw.transactionId : undefined,
    stripePaymentIntentId: typeof raw.stripePaymentIntentId === 'string' ? raw.stripePaymentIntentId : undefined,
    sumupCheckoutId: typeof raw.sumupCheckoutId === 'string' ? raw.sumupCheckoutId : undefined,
    processingFee,
    subtotal: raw.subtotal !== undefined ? Number(raw.subtotal) : undefined,
    vatAmount: raw.vatAmount !== undefined ? Number(raw.vatAmount) : undefined,
    transactionFee: raw.transactionFee !== undefined ? Number(raw.transactionFee) : undefined,
    accountingSplit: raw.accountingSplit as Payment['accountingSplit'],
    stripeChargeId: typeof raw.stripeChargeId === 'string' ? raw.stripeChargeId : undefined,
    sumupTransactionCode: typeof raw.sumupTransactionCode === 'string' ? raw.sumupTransactionCode : undefined,
    slickpayInvoiceId: typeof raw.slickpayInvoiceId === 'string' ? raw.slickpayInvoiceId : undefined,
    voided: raw.voided === true,
    voidedAt:
      raw.voidedAt && typeof raw.voidedAt === 'string'
        ? new Date(raw.voidedAt)
        : raw.voidedAt instanceof Date
          ? raw.voidedAt
          : undefined,
    voidedByUid: typeof raw.voidedByUid === 'string' ? raw.voidedByUid : undefined,
  };
}

export function mapCallableInvoiceToInvoice(payload: CallableInvoicePayload): Invoice {
  const ph = (payload.paymentHistory || []).map((row) => parsePaymentRow(row));
  return {
    id: payload.id,
    eventId: payload.eventId || '',
    unitId: payload.unitId || '',
    ownerUid: payload.ownerUid || '',
    totalAmount: Number(payload.totalAmount) || 0,
    amountPaid: Number(payload.amountPaid) || 0,
    outstandingBalance: Number(payload.outstandingBalance) || 0,
    status: (payload.status as Invoice['status']) || 'outstanding',
    rsvps: payload.rsvps || [],
    paymentHistory: ph,
    paymentLink: payload.paymentLink || '',
    remindersSent: [],
    createdAt: payload.createdAt ? new Date(payload.createdAt) : new Date(),
    updatedAt: payload.updatedAt ? new Date(payload.updatedAt) : new Date(),
    invoiceNumber: payload.invoiceNumber,
    accountingTotals: payload.accountingTotals,
    subtotal: payload.subtotal,
    vatAmount: payload.vatAmount,
    ...(payload.allInPriceBreakdown
      ? {
          allInPriceBreakdown: {
            ticketPrice: Number(payload.allInPriceBreakdown.ticketPrice) || 0,
            creditCostRecovery: Number(payload.allInPriceBreakdown.creditCostRecovery) || 0,
          },
        }
      : {}),
    ...(payload.pendingVerificationAmount !== undefined
      ? { pendingVerificationAmount: Number(payload.pendingVerificationAmount) || 0 }
      : {}),
  };
}

type InvoicePaymentsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: Event | null;
  invoice: Invoice | null;
  /** When set, ticket and booking fee lines use current event rules (paid invoices are not rewritten in Firestore). */
  rsvps?: RSVP[];
  onInvoiceUpdated: (invoice: Invoice) => void;
};

export function InvoicePaymentsModal({
  open,
  onOpenChange,
  event,
  invoice,
  rsvps,
  onInvoiceUpdated,
}: InvoicePaymentsModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addAmount, setAddAmount] = useState('');

  useEffect(() => {
    if (!open) {
      setShowAddForm(false);
      setAddAmount('');
    }
  }, [open]);

  const guestCount =
    invoice && rsvps?.length
      ? countCanonicalInvoiceGuests(invoice, rsvps)
      : invoice?.rsvps?.length ?? 0;

  const amountPaidDisplay = invoice ? getEffectiveAmountPaid(invoice) : 0;
  const amountReserved = invoice ? getAmountReservedOnInvoice(invoice) : 0;
  const pendingClaimAmount = invoice ? getPendingVerificationAmount(invoice) : 0;

  const livePricingBreakdown = useMemo(() => {
    if (!event || !invoice || !rsvps?.length || !invoice.rsvps?.length) {
      return null;
    }
    const ordered = orderRsvpsForInvoice(invoice, rsvps);
    if (ordered.length === 0) {
      return null;
    }
    const payer = getInvoicePayerRsvp(ordered[0], ordered);
    const ticket = computeInvoiceBaseAmountForPayer(payer, event, rsvps);
    const slots = countHostInvoiceCreditRecoverySlots(payer, event, rsvps);
    const feePence = getCreditBookingFeePenceForEvent(event);
    const booking =
      ticket > 0 ? computeCreditRecoveryTotalPounds(event, ticket, slots, feePence) : 0;
    const policyTotal = computeInvoicePolicyDisplayTotal(invoice, event, rsvps);
    return { ticket, booking, policyTotal };
  }, [event, invoice, rsvps]);

  const displayTotalAmount = livePricingBreakdown?.policyTotal ?? invoice?.totalAmount ?? 0;

  const outstanding = useMemo(() => {
    if (!invoice) return 0;
    if (livePricingBreakdown) {
      return Math.max(0, Math.round((livePricingBreakdown.policyTotal - amountReserved) * 100) / 100);
    }
    const storedTotal = Number(invoice.totalAmount) || 0;
    if (storedTotal > INVOICE_AMOUNT_EPS) {
      return Math.max(0, Math.round((storedTotal - amountReserved) * 100) / 100);
    }
    return Math.max(0, Number(invoice.outstandingBalance) || 0);
  }, [invoice, livePricingBreakdown, amountReserved]);

  const amountToRecordOnApprove = useMemo(() => {
    if (!invoice) return 0;
    if (invoice.status === 'pending_verification' && pendingClaimAmount > INVOICE_AMOUNT_EPS) {
      return pendingClaimAmount;
    }
    return outstanding;
  }, [invoice, outstanding, pendingClaimAmount]);

  const canRecord = Boolean(
    invoice &&
      event &&
      invoice.status !== 'voided' &&
      (invoice.status === 'pending_verification'
        ? pendingClaimAmount > INVOICE_AMOUNT_EPS
        : outstanding > INVOICE_AMOUNT_EPS),
  );

  const recordPayment = useCallback(
    async (amount: number) => {
      if (!event || !invoice) {
        return;
      }
      if (amount <= 0 || !Number.isFinite(amount)) {
        showError('Enter a valid amount');
        return;
      }
      const maxRecordable =
        invoice.status === 'pending_verification' && pendingClaimAmount > INVOICE_AMOUNT_EPS
          ? Math.max(outstanding, pendingClaimAmount)
          : outstanding;
      if (amount - maxRecordable > INVOICE_AMOUNT_EPS) {
        showError(`Amount cannot exceed outstanding (${formatCurrency(maxRecordable)})`);
        return;
      }

      setSubmitting(true);
      try {
        const fn = httpsCallable(functions, 'recordManualInvoicePayment');
        const result = await fn({
          unitId: event.unitId,
          eventId: event.id,
          invoiceId: invoice.id,
          amount,
        });
        const data = result.data as { success?: boolean; invoice?: CallableInvoicePayload };
        if (data?.success && data.invoice) {
          onInvoiceUpdated(mapCallableInvoiceToInvoice(data.invoice));
          showSuccess('Payment recorded');
          setShowAddForm(false);
          setAddAmount('');
        } else {
          showError('Unexpected response from server');
        }
      } catch (e: unknown) {
        const err = e as { message?: string };
        console.error(e);
        showError(err.message || 'Failed to record payment');
      } finally {
        setSubmitting(false);
      }
    },
    [event, invoice, outstanding, pendingClaimAmount, onInvoiceUpdated],
  );

  const handleMarkAsPaid = async () => {
    if (!canRecord || amountToRecordOnApprove <= INVOICE_AMOUNT_EPS) {
      return;
    }
    const isPendingVerification = invoice?.status === 'pending_verification';
    const recordAmount = Math.round(amountToRecordOnApprove * 100) / 100;
    const result = await confirmAction({
      title: isPendingVerification ? 'Approve Bank Transfer' : 'Mark as paid',
      text: isPendingVerification
        ? `Confirm the bank transfer of ${formatCurrency(recordAmount)} has been received and approve this payment?`
        : `Record a payment of ${formatCurrency(recordAmount)} to settle this invoice?`,
      confirmButtonText: isPendingVerification ? 'Approve' : 'Record payment',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) {
      return;
    }
    await recordPayment(recordAmount);
  };

  const handleAddPaymentSubmit = async () => {
    const parsed = parseFloat(addAmount.replace(/,/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showError('Enter a valid amount');
      return;
    }
    await recordPayment(Math.round(parsed * 100) / 100);
  };

  const handleVoidPayment = async (index: number, amount: number) => {
    if (!event || !invoice) {
      return;
    }
    const result = await confirmAction({
      title: 'Void payment?',
      text: `Remove ${formatCurrency(amount)} from recorded takings? The invoice balance will be updated. This cannot be undone.`,
      confirmButtonText: 'Void payment',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) {
      return;
    }

    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'voidInvoicePayment');
      const response = await fn({
        unitId: event.unitId,
        eventId: event.id,
        invoiceId: invoice.id,
        paymentHistoryIndex: index,
      });
      const data = response.data as { success?: boolean; invoice?: CallableInvoicePayload };
      if (data?.success && data.invoice) {
        onInvoiceUpdated(mapCallableInvoiceToInvoice(data.invoice));
        showSuccess('Payment voided');
      } else {
        showError('Unexpected response from server');
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error(e);
      showError(err.message || 'Failed to void payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payments</DialogTitle>
            <DialogDescription>
              Invoice totals and recorded payments. Secretary, Treasurer, or platform admin can add payments.
            </DialogDescription>
          </DialogHeader>

          {!invoice || !event ? (
            <p className="text-sm text-muted-foreground">No invoice linked to this booking.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Total guests</div>
                  <div className="text-lg font-semibold">{guestCount}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Total amount</div>
                  <div className="text-lg font-semibold">{formatCurrency(displayTotalAmount)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Amount paid</div>
                  <div className="text-lg font-semibold">{formatCurrency(amountPaidDisplay)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Outstanding</div>
                  <div className="text-lg font-semibold">{formatCurrency(outstanding)}</div>
                </div>
              </div>

              {livePricingBreakdown ? (
                <div className="rounded-md bg-muted/50 p-3 text-sm space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Ticket and {BOOKING_FEE_LABEL.toLowerCase()} use current event settings. Stored invoice total is
                    unchanged after payment.
                  </p>
                  <div className="flex justify-between py-1">
                    <span className="text-muted-foreground font-medium">Ticket price (total)</span>
                    <span className="font-semibold">{formatCurrency(livePricingBreakdown.ticket)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-t border-muted">
                    <span className="text-muted-foreground font-medium">{BOOKING_FEE_LABEL}</span>
                    <span className="font-semibold">{formatCurrency(livePricingBreakdown.booking)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-t border-muted">
                    <span className="text-muted-foreground font-medium">Policy total (tickets + fee + tx)</span>
                    <span className="font-semibold">{formatCurrency(livePricingBreakdown.policyTotal)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-t border-muted mt-1">
                    <span className="font-bold">Recorded invoice total</span>
                    <span className="font-bold">{formatCurrency(invoice.totalAmount)}</span>
                  </div>
                </div>
              ) : invoice.allInPriceBreakdown ? (
                <div className="rounded-md bg-muted/50 p-3 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-muted-foreground font-medium">Ticket Price (Total)</span>
                    <span className="font-semibold">{formatCurrency(invoice.allInPriceBreakdown.ticketPrice)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-t border-muted">
                    <span className="text-muted-foreground font-medium">{BOOKING_FEE_LABEL}</span>
                    <span className="font-semibold">{formatCurrency(invoice.allInPriceBreakdown.creditCostRecovery)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-t border-muted mt-1">
                    <span className="font-bold">Total Amount</span>
                    <span className="font-bold">{formatCurrency(invoice.totalAmount)}</span>
                  </div>
                </div>
              ) : null}

              {invoice.status === 'pending_verification' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                    Pending bank transfer verification. The member has claimed payment. Approve once the transfer has cleared in the bank account.
                  </p>
                </div>
              )}

              <div>
                <h4 className="mb-2 text-sm font-medium">Payment history</h4>
                {invoice.paymentHistory && invoice.paymentHistory.length > 0 ? (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="hidden sm:table-cell">Reference</TableHead>
                          <TableHead className="w-[100px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoice.paymentHistory.map((p, idx) => (
                          <TableRow
                            key={`${idx}-${p.date?.toString?.()}-${p.amount}`}
                            className={p.voided ? 'opacity-60' : undefined}
                          >
                            <TableCell className="whitespace-nowrap text-sm">
                              {formatDate(p.date)}
                              {p.voided ? (
                                <span className="ml-2 text-xs font-medium text-destructive">Voided</span>
                              ) : null}
                            </TableCell>
                            <TableCell className="text-sm">{paymentMethodLabel(p.method)}</TableCell>
                            <TableCell className="text-right text-sm">
                              <span className={p.voided ? 'line-through' : undefined}>{formatCurrency(p.amount)}</span>
                            </TableCell>
                            <TableCell className="hidden max-w-[12rem] truncate font-mono text-xs sm:table-cell">
                              {paymentReference(p)}
                            </TableCell>
                            <TableCell className="text-right">
                              {!p.voided && invoice.status !== 'voided' ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  disabled={submitting}
                                  onClick={() => void handleVoidPayment(idx, p.amount)}
                                >
                                  <Ban className="mr-1 h-3.5 w-3.5" />
                                  Void
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {invoice.status === 'pending_verification' ? (
                  <Button
                    type="button"
                    variant="default"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={!canRecord || submitting}
                    onClick={() => void handleMarkAsPaid()}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve Bank Transfer
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="default"
                    disabled={!canRecord || submitting}
                    onClick={() => void handleMarkAsPaid()}
                  >
                    Mark as paid
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canRecord || submitting}
                  onClick={() => {
                    setAddAmount(outstanding > 0 ? outstanding.toFixed(2) : '');
                    setShowAddForm(true);
                  }}
                >
                  Add payment
                </Button>
              </div>

              {showAddForm && (
                <div className="rounded-md border p-4 space-y-3">
                  <div className="text-sm font-medium">Add payment</div>
                  <div className="space-y-2">
                    <Label htmlFor="manual-payment-amount">Amount (GBP)</Label>
                    <Input
                      id="manual-payment-amount"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={addAmount}
                      onChange={(e) => setAddAmount(e.target.value)}
                      disabled={submitting}
                    />
                    <p className="text-xs text-muted-foreground">
                      Outstanding: {formatCurrency(outstanding)}. Amount cannot exceed this figure.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowAddForm(false);
                        setAddAmount('');
                      }}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button type="button" size="sm" onClick={() => void handleAddPaymentSubmit()} disabled={submitting}>
                      {submitting ? 'Saving…' : 'Submit'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
}
